// User-facing message rendered in DriverSelectorCell's dispatch warning dialog
// when needsNotRailbilledWarning() returns true.
export const NOT_RAILBILLED_WARNING_MESSAGE = 'Warning: Not Railbilled';

/**
 * Checks whether a driver assignment should trigger a "Not Railbilled" warning.
 *
 * The backend (orders.py) computes this at the order level using:
 *   - If the order has a terminate leg → check the terminate leg's dest railbilled
 *   - Else (no terminate leg) → check the delivery leg's dest railbilled
 * The result lives on `order.warnings` as `{ code: 'not_railbilled', ... }`. We
 * don't re-implement that SQL — we just consume the flag.
 *
 * At the cell level, we then scope it to the leg the backend was evaluating, so
 * a PRO with a multi-leg dispatch only fires the prompt on the leg that
 * actually delivers to the rail location:
 *   1. Backend must have flagged `not_railbilled` on the order.
 *   2. The dispatch group must contain a TERMINATE leg, OR
 *      the order has no TERMINATE leg anywhere and the dispatch group contains
 *      its DELIVERY leg (the fallback case).
 *
 * `allOrderLegs` is optional. When omitted we treat `dispatchGroupLegs` as the
 * full leg list — correct for surfaces like ImportsCard / ExportsCard where one
 * dispatch row corresponds to the whole order. For scope-aware surfaces
 * (LegsGrid, OrderDetailsModalContent), pass both so we can distinguish "this
 * dispatch is the DELIVERY leg of a P→D→T order" from "this is a DELIVERY-only
 * order with no TERMINATE."
 */
export function needsNotRailbilledWarning(params: {
  warnings?: { code: string; message?: string }[];
  dispatchGroupLegs?: { legtype?: string }[];
  allOrderLegs?: { legtype?: string }[];
}): boolean {
  const { warnings, dispatchGroupLegs, allOrderLegs } = params;
  if (!warnings?.some((w) => w.code === 'not_railbilled')) return false;
  if (!dispatchGroupLegs || dispatchGroupLegs.length === 0) return false;

  const hasLegtype = (legs: { legtype?: string }[], type: string) =>
    legs.some((l) => l.legtype && l.legtype.toUpperCase() === type);

  if (hasLegtype(dispatchGroupLegs, 'TERMINATE')) return true;

  // Fallback: delivery-only orders (no terminate leg) — the rail check applies
  // to the delivery leg. Use allOrderLegs when provided to confirm no TERMINATE
  // exists elsewhere in the order; otherwise treat the dispatch group as the
  // full order.
  const fullLegList = allOrderLegs ?? dispatchGroupLegs;
  if (hasLegtype(fullLegList, 'TERMINATE')) return false;
  return hasLegtype(dispatchGroupLegs, 'DELIVERY');
}
